import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import PasswordField from '../components/PasswordField';
import {
  confirmForgotPassword,
  forgotPassword,
  login,
  translateAuthError,
  type AuthTokens,
} from '../lib/auth';
import { useAuth } from '../lib/AuthContext';
import { afterLoginTarget } from '../lib/afterLoginTarget';

// ADR-0006/D-24: 4 Ansichten -- normales Login, das bereits bestehende "neues Passwort setzen"
// (Erstanmeldung eines eingeladenen Nutzers), sowie zwei neue Schritte für den self-service
// Passwort-Reset (per E-Mail-Code), sowohl proaktiv ("Passwort vergessen?") als auch automatisch
// nach einem Admin-initiierten Reset (Cognito wirft dabei PasswordResetRequiredException, ohne
// dass bereits ein Code verschickt wurde -- forgotPassword() muss das selbst noch auslösen).
type View = 'login' | 'newPasswordRequired' | 'forgotRequest' | 'forgotConfirm';

export default function LoginPage() {
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [challenge, setChallenge] = useState<{
    completeNewPassword: (newPassword: string) => Promise<AuthTokens>;
  }>();
  const [error, setError] = useState<string>();
  const [info, setInfo] = useState<string>();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const target = afterLoginTarget(useLocation().state);

  useEffect(() => {
    setError(undefined);
  }, [email, password, newPassword, forgotCode, forgotNewPassword]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setInfo(undefined);
    try {
      const result = await login(email, password);
      if (result.status === 'NEW_PASSWORD_REQUIRED') {
        setChallenge(result);
        setView('newPasswordRequired');
        return;
      }
      if (result.status === 'PASSWORD_RESET_REQUIRED') {
        // Ein Admin hat einen Reset ausgelöst -- noch kein Code verschickt, das holen wir hier nach.
        await forgotPassword(email).catch(() => undefined);
        setInfo(
          'Ihr Administrator hat einen Passwort-Reset veranlasst. Wir haben Ihnen einen Code per E-Mail geschickt.',
        );
        setView('forgotConfirm');
        return;
      }
      await refresh();
      navigate(target, { replace: true });
    } catch (err) {
      setError(translateAuthError(err, 'Anmeldung fehlgeschlagen'));
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(undefined);
    try {
      await challenge.completeNewPassword(newPassword);
      await refresh();
      navigate(target, { replace: true });
    } catch (err) {
      setError(translateAuthError(err, 'Passwort konnte nicht gesetzt werden'));
    }
  }

  async function handleForgotRequest(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    // Bewusst kein Fehler nach außen, egal was passiert -- verrät sonst, ob die Adresse existiert.
    await forgotPassword(email).catch(() => undefined);
    setInfo('Falls die Adresse existiert, wurde ein Code per E-Mail versendet.');
    setView('forgotConfirm');
  }

  async function handleForgotConfirm(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    try {
      await confirmForgotPassword(email, forgotCode, forgotNewPassword);
      setInfo('Passwort erfolgreich gesetzt. Bitte melden Sie sich an.');
      setPassword('');
      setForgotCode('');
      setForgotNewPassword('');
      setView('login');
    } catch (err) {
      setError(translateAuthError(err, 'Code oder Passwort ungültig'));
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }} elevation={1}>
        <Box
          component="img"
          src="/club-logo.png"
          alt="Sp.Vgg. Lagerlechfeld-Graben"
          sx={{ height: 72, display: 'block', mx: 'auto', mb: 2 }}
        />
        <Typography variant="h5" component="h1" sx={{ textAlign: 'center' }}>
          MyHours
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ textAlign: 'center', mb: 2 }}
        >
          Deine Online Stundenerfassung
        </Typography>

        {view === 'newPasswordRequired' && (
          <Box component="form" onSubmit={handleNewPassword} noValidate>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Bitte legen Sie ein neues Passwort fest.
              </Typography>
              <PasswordField
                label="Neues Passwort"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large">
                Passwort setzen
              </Button>
            </Stack>
          </Box>
        )}

        {view === 'forgotRequest' && (
          <Box component="form" onSubmit={handleForgotRequest} noValidate>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Geben Sie Ihre E-Mail-Adresse ein, wir schicken Ihnen einen Code zum Zurücksetzen.
              </Typography>
              <TextField
                label="E-Mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoFocus
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large">
                Code anfordern
              </Button>
              <Button variant="text" onClick={() => setView('login')}>
                Zurück zur Anmeldung
              </Button>
            </Stack>
          </Box>
        )}

        {view === 'forgotConfirm' && (
          <Box component="form" onSubmit={handleForgotConfirm} noValidate>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {info && <Alert severity="info">{info}</Alert>}
              <TextField
                label="Code aus der E-Mail"
                value={forgotCode}
                onChange={(e) => setForgotCode(e.target.value)}
                required
                fullWidth
                autoFocus
                autoComplete="one-time-code"
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <PasswordField
                label="Neues Passwort"
                value={forgotNewPassword}
                onChange={(e) => setForgotNewPassword(e.target.value)}
                required
                fullWidth
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large">
                Passwort zurücksetzen
              </Button>
              <Button variant="text" onClick={() => setView('login')}>
                Zurück zur Anmeldung
              </Button>
            </Stack>
          </Box>
        )}

        {view === 'login' && (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {info && <Alert severity="success">{info}</Alert>}
              <TextField
                label="E-Mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoFocus
              />
              <PasswordField
                label="Passwort"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large">
                Anmelden
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setError(undefined);
                  setInfo(undefined);
                  setView('forgotRequest');
                }}
              >
                Passwort vergessen?
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
